import { useParams, Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import MangaCard from '@/components/base/MangaCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getMangaByCategory, mangaList } from '@/mocks/manga';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'instant' });
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const mangas = getMangaByCategory(slug || '');
  const displayName = currentCategory?.name || '漫画';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${displayName}漫画 - 韩国漫画网`,
    'description': `韩国漫画网${displayName}漫画分类，收录${mangas.length}部热门${displayName}韩漫，全网最快更新，为您推荐最优质的${displayName}类韩国漫画。`,
    'url': `https://www.koreanmanhwa.cn/category/${slug}`,
    'mainEntity': {
      '@type': 'ItemList',
      'itemListElement': mangas.map((manga, idx) => ({
        '@type': 'ListItem',
        'position': idx + 1,
        'item': {
          '@type': 'Book',
          'name': manga.title,
          'url': `https://www.koreanmanhwa.cn/article/${manga.id}`,
        },
      })),
    },
  };

  return (
    <div ref={scrollRef}>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <div className="bg-background-100/70 pt-20 pb-8">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览' },
              { label: `${displayName}漫画` },
            ]}
          />
          <div className="mt-4 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className={`${currentCategory?.icon || 'ri-book-line'} text-lg`}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{displayName}漫画</h1>
              <p className="text-sm text-foreground-500 mt-0.5">
                共 <strong>{mangaList.filter((m) => m.categorySlug === slug).length}</strong> 部作品
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
          <Link
            to="/category/romance"
            className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors border cursor-pointer ${
              slug === 'romance'
                ? 'bg-primary-500 text-background-50 border-primary-500'
                : 'bg-background-50 text-foreground-600 border-background-200 hover:border-primary-300'
            }`}
          >
            全部
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors border cursor-pointer ${
                slug === cat.slug
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-600 border-background-200 hover:border-primary-300'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      <section className="px-4 md:px-6 py-6 md:py-10 max-w-7xl mx-auto">
        {mangas.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {mangas.map((manga) => (
              <MangaCard
                key={manga.id}
                id={manga.id}
                title={manga.title}
                coverImage={manga.coverImage}
                category={manga.category}
                categorySlug={manga.categorySlug}
                rating={manga.rating}
                chapters={manga.chapters}
                updatedAt={manga.updatedAt}
                status={manga.status}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="mt-4 text-foreground-500">该分类暂无漫画内容</p>
            <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        )}
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">其他分类</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <div>
                    <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                    <span className="text-xs text-foreground-400 ml-2">{cat.count}部</span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}